/**
 * Helper functions to define whether browser is supported or not.
 */

// https://github.com/faisalman/ua-parser-js (see also: https://www.whatsmyua.info/)
const { UAParser } = require("ua-parser-js");

// Name of the storage key.
const BROWSER_ACCEPTED = "browser_accepted";

// Read and write persistent browser information.
export function acceptBrowser(accepted) {
  sessionStorage.setItem(BROWSER_ACCEPTED, accepted);
}
export function isBrowserAccepted() {
  return sessionStorage.getItem(BROWSER_ACCEPTED) === "true";
}
export function shouldAskAboutBrowser() {
  return sessionStorage.getItem(BROWSER_ACCEPTED) === null;
}

// Browser version matrix. We may want to move that into a config module
// or JSON setting somewhere more accessible.
// For a complete list of browsers that can be detected from the user agents string:
// https://github.com/faisalman/ua-parser-js#methods
const supportedBrowsers = [
  { name: "Chrome", version: "69.0.0" }, // Jan 2018
  { name: "Firefox", version: "76.0.0" }, // May 2018
  { name: "Safari", version: "11.0.0" }, // Sep 2018 (adds WebRTC support for Tokbox)
  { name: "Edge", version: "80.0.0" }, // May 2019 (based on Chromium)
  { name: "Electron", version: "" }, // used for e2e tests
];

// OS matrix of supported systems. We may want to move that into a config
// module or JSON setting somewhere more accessible.
// For a complete list of OSs that can be detected from the user agents string:
// https://github.com/faisalman/ua-parser-js#methods
const supportedOSs = [
  "Mac OS",
  "Fedora",
  "Debian",
  "Ubuntu",
  "Linux",
  "Windows 7",
  "Windows 8",
  "Windows 8.1",
  "Windows 10",
  "Windows",
  "iOS",
  // "Chrome OS",
  "Android OS",
  "Android",
];

// Global browser information for reuse.
var browser = undefined;

/**
 * Helper function to compare two semantic version strings of the form "MM.mm.pp".
 * Return 0 if both version strings are the same; -1 if the first string is less
 * than the second one; and 1 if the first string is greater than the second one.
 * TODO a prettier way would be to normalize the version strings to, e.g. MMM.mmmmm.ppppp
 * format, remove the . and treat the version numbers as plain integers.
 *
 * @param {string} versionStr - is a semantic version string.
 * @param {string} otherVersionStr - is a semantic version string.
 * @returns {int}
 */
function compareVersions(versionStr, otherVersionStr) {
  // Semantic version string to three integer values.
  // TODO handle version strings of the form "MM.mm" and "MM".
  const [major, minor, patch] = versionStr.split(".").map(e => parseInt(e));
  const [otherMajor, otherMinor, otherPatch] = otherVersionStr
    .split(".")
    .map(e => parseInt(e));

  // Now compare the version values.
  if (major < otherMajor) {
    return -1;
  } else if (major > otherMajor) {
    return 1;
  } else {
    if (minor < otherMinor) {
      return -1;
    } else if (minor > otherMinor) {
      return 1;
    } else {
      if (patch < otherPatch) {
        return -1;
      } else if (patch > otherPatch) {
        return 1;
      } else {
        return 0;
      }
    }
  }
}

/**
 * Detect and return information about the current browser. Note that the this
 * information is unreliable as it can be spoofed by the client; however, we
 * trust that it's ok or else the client who spoofs the UA string will have a
 * bad time.
 *
 * @returns a browser object.
 */
export default function getBrowserInfo() {
  if (browser) {
    return browser;
  }

  // Determine and add `supported` flags, first for browser, os, device, and then the
  // overall cumulation for all.
  var ua = UAParser();
  ua.browser.supported = supportedBrowsers.some(function (supportedBrowser) {
    return (
      ua.browser.name === supportedBrowser.name &&
      compareVersions(ua.browser.version, supportedBrowser.version) >= 0
    );
  });
  ua.os.supported = supportedOSs.includes(ua.os.name);
  ua.supported = ua.browser.supported && ua.os.supported;

  // Cache and return the browser object.
  browser = ua;
  return browser;
}
